// src/apis/uploadApi.js
import axiosInstance from "../config/axiosInstance";
import { ASSET_BASE_URL } from "../constants/apiEndpoints";

const LOCAL_UPLOAD_ORIGIN_PATTERN = /^https?:\/\/(?:localhost|127\.0\.0\.1):\d+/i;

/**
 * Normalize một URL ảnh hoặc một đoạn HTML từ Quill chứa các thẻ <img> từ backend:
 * - Nếu input chứa chuỗi HTML (có thẻ <img ...>): tự động quét tất cả src="..." và normalizeURL từng ảnh
 * - Nếu input là URL ảnh đơn:
 *   - Nếu URL chứa origin localhost/127.0.0.1 -> loại bỏ origin, chỉ giữ relative path /upload/...
 *   - Nếu URL là backend upload path (/upload/...) -> gắn thêm ASSET_BASE_URL (domain backend)
 *   - Mọi trường hợp còn lại (static import, https, data:, blob:) -> giữ nguyên
 */
export const normalizeUploadedImageUrl = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== "string") return imageUrl || "";

    // Nếu input là HTML string từ Quill Editor chứa thẻ <img ...>
    if (imageUrl.includes("<img")) {
        return imageUrl.replace(/<img\s+([^>]*?)src=["']([^"']+)["']/gi, (match, prefix, srcUrl) => {
            const normalizedSrc = normalizeUploadedImageUrl(srcUrl);
            return `<img ${prefix}src="${normalizedSrc}"`;
        });
    }

    // Nếu URL có chứa origin localhost/127.0.0.1 -> loại bỏ origin, chỉ giữ /upload/...
    if (LOCAL_UPLOAD_ORIGIN_PATTERN.test(imageUrl)) {
        imageUrl = imageUrl.replace(LOCAL_UPLOAD_ORIGIN_PATTERN, "");
    }
    // Nếu URL đã là absolute (http/https) hoặc là data: / blob: -> giữ nguyên
    if (/^https?:\/\//i.test(imageUrl) || /^(data:|blob:)/i.test(imageUrl)) {
        return imageUrl;
    }
    // Chỉ gắn ASSET_BASE_URL nếu là path upload từ backend (/upload/...)
    if (imageUrl.startsWith("/upload/") && ASSET_BASE_URL) {
        const base = ASSET_BASE_URL.replace(/\/$/, "");
        return `${base}${imageUrl}`;
    }
    return imageUrl;
};

export const normalizeQuillHtml = normalizeUploadedImageUrl;


/**
 * Upload một file ảnh lên Cloudinary thông qua Backend API.
 *
 * @param {File} file - File ảnh cần upload.
 * @returns {Promise<string>} - URL của ảnh trên Cloudinary.
 * @throws {Error} - Ném lỗi nếu API thất bại hoặc trả về success: false.
 */
export const uploadImage = async (file, options = {}) => {
    // Backend dùng multer với upload.single('image')
    // nên field name trong FormData phải là 'image'
    const formData = new FormData();
    formData.append("image", file);
    if (options.folder) formData.append("folder", options.folder);

    const response = await axiosInstance.post(
        "/admin/upload-images",
        formData,
        {
            headers: {
                // Ghi đè Content-Type để axiosInstance tự set boundary cho multipart
                "Content-Type": "multipart/form-data",
            },
        }
    );

    const result = response?.data;

    if (!result || result.success === false) {
        throw new Error(result?.message || "Upload ảnh thất bại!");
    }

    const imageUrl = result?.data?.imageUrl;
    if (!imageUrl) {
        throw new Error("API không trả về URL ảnh hợp lệ.");
    }

    return normalizeUploadedImageUrl(imageUrl);
};
